/**
 * Identidade legível de um modelo de IA — espelho do `describe_model` do backend
 * (`backend/apps/agents/model_identity.py`). As duas pontas precisam dizer a MESMA coisa:
 * o badge da tela, o rodapé da resposta e o próprio bot, quando perguntado.
 *
 * Regra de honestidade: modelo fora do catálogo não ganha fornecedor inventado — mostra o
 * id cru do deployment e `known: false`. Um deployment pode ser renomeado ou re-apontado
 * pelo portal do Azure sem passar por code review; o que a tela afirma é só o que sabemos.
 */

export interface ModelCatalogEntry {
  label?: string;
  provider?: string;
  tier?: string;
}

export type ModelCatalog = Record<string, ModelCatalogEntry>;

export interface ModelIdentity {
  id: string;
  /** Nome do modelo pro usuário ("GPT-5.4"). Cai no id cru quando não catalogado. */
  label: string;
  /** Fornecedor ("OpenAI"). Vazio quando não catalogado — nunca chutado. */
  provider: string;
  /** Apelido comercial ("Econômico"), secundário. */
  tier: string;
  /** Texto pronto: "GPT-5.4 · OpenAI". */
  full: string;
  /** Só do resolvedor local: o id estava no catálogo? Ausente quando a identidade veio
   *  pronta do servidor (que já não devolve nada para modelo não catalogado). */
  known?: boolean;
}

export function describeModel(
  modelId?: string | null,
  catalog?: ModelCatalog | null,
): ModelIdentity | null {
  const id = (modelId ?? '').trim();
  if (!id) return null;

  const entry = catalog?.[id];
  const label = entry?.label?.trim() || id;
  const provider = entry?.provider?.trim() || '';

  return {
    id,
    label,
    provider,
    tier: entry?.tier?.trim() || '',
    full: provider ? `${label} · ${provider}` : label,
    known: !!entry,
  };
}
